import * as React from 'react';
import {createViewConfig} from '@sail/engine';
import type {ReactNode} from 'react';
import {view, type View} from '../view';
import {css, external} from '../css';
import type {TypographyEmphasis} from './typography';

type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div';
type HeadingSize = 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall';
type HeadingAlign = 'start' | 'end' | 'center' | 'justify';

const DEFAULT_SIZE = 'small';
const DEFAULT_EMPHASIS = 'emphasized';
const DEFAULT_ALIGN = 'start';

export type HeadingProps = View.IntrinsicElement<
  'h2',
  {
    /**
     * Controls horizontal text alignment.
     */
    align: HeadingAlign;
    /**
     * Override the semantic HTML element for document hierarchy.
     */
    as: HeadingElement;
    /**
     * Use different sizes to create clear hierarchies.
     */
    size: HeadingSize;
    /**
     * Controls typographic prominence.
     */
    emphasis: TypographyEmphasis;
    /**
     * Heading content.
     */
    children: ReactNode;
    /**
     * Allow data-* attributes
     */
    [key: `data-${string}`]: unknown;
  }
>;

/** @internal */
export const HeadingConfig = createViewConfig({
  name: 'Heading',
  props: {} as HeadingProps,
  flattens: true,
  defaults: {
    align: 'start',
    as: 'div',
    size: DEFAULT_SIZE,
    emphasis: DEFAULT_EMPHASIS,
  },
});

const elements = {
  h1: view.h1,
  h2: view.h2,
  h3: view.h3,
  h4: view.h4,
  h5: view.h5,
  h6: view.h6,
  div: view.div,
};

const typographyStyles = {
  xlarge: {
    normal: css({font: 'heading.xlarge.subdued'}),
    emphasized: css({font: 'heading.xlarge'}),
  },
  large: {
    normal: css({font: 'heading.large.subdued'}),
    emphasized: css({font: 'heading.large'}),
  },
  medium: {
    normal: css({font: 'heading.medium.subdued'}),
    emphasized: css({font: 'heading.medium'}),
  },
  small: {
    normal: css({font: 'heading.small.subdued'}),
    emphasized: css({font: 'heading.small'}),
  },
  xsmall: {
    normal: css({font: 'heading.xsmall.subdued'}),
    emphasized: css({font: 'heading.xsmall'}),
  },
};

const baseStyles = css({
  color: 'text',
  margin: 'space.0',
});

const alignStyles = {
  start: css({textAlign: 'start'}),
  end: css({textAlign: 'end'}),
  center: css({textAlign: 'center'}),
  justify: css({textAlign: 'justify'}),
};

/**
 * Headings are used to create levels of typographic hierarchies within a page.
 *
 * @see: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Heading_Elements
 */
export const Heading = HeadingConfig.createView(
  ({align, as, size, emphasis, ...props}) => {
    const HeadingTag = elements[as];
    const styles = React.useMemo(
      () => [
        baseStyles,
        typographyStyles[size ?? DEFAULT_SIZE][emphasis ?? DEFAULT_EMPHASIS],
        alignStyles[align ?? DEFAULT_ALIGN],
      ],
      [align, size, emphasis],
    );

    return <HeadingTag css={external(styles)} {...props} />;
  },
);
